import React, { useState } from 'react';
import { Avatar, Box, Button, Typography } from '@mui/material';
import { useAuth } from '../services/AuthContext';
import { C, FONT, glow, hexToRgba, prefersReducedMotion } from '../theme/tokens';
import WormholeGate from '../components/WormholeGate';
import WormholeVoid from '../components/WormholeVoid';
import Gate from './Gate';

/** Whatever deep link brought the visitor here, so SSO can send them back
 * to it instead of dumping everyone on the dashboard. This is what makes
 * pasting `/system/J224754` into corp chat work for a member who isn't
 * signed in yet: gate -> EVE SSO -> that system's page.
 *
 * The backend re-validates this (`isSafeReturnTo` in routes/auth.ts only
 * accepts a same-origin absolute path), so a crafted link can't turn the
 * login flow into an open redirect. `/` returns undefined so the normal
 * login lands on the dashboard with no redirect at all. */
function currentPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const path = window.location.pathname + window.location.search;
  return path === '/' ? undefined : path;
}

/** The login CTA's own wedge glyph — deliberately NOT the shared `Chevron`
 * component. `Chevron.tsx` was redrawn app-wide into a flat square as part
 * of the v11 redesign; since this page is explicitly excluded from that
 * redesign, importing the shared component would have silently swapped
 * this button's icon out from under it (which is exactly what had already
 * happened before this fix — the wedge had quietly become a square).
 * Keeping this page's own copy of the original wedge shape is what
 * actually makes "untouched" durable against future changes to the shared
 * component. `pulse` only toggles opacity, never scale — see the button's
 * own comment on why nothing here may change size on hover/lock. */
const LoginChevron: React.FC<{ pulse?: boolean }> = ({ pulse }) => (
  <Box
    component="svg"
    viewBox="0 0 10 10"
    aria-hidden
    sx={{
      width: 11,
      height: 11,
      flexShrink: 0,
      filter: `drop-shadow(0 0 3px ${C.textOnFill})`,
      animation: pulse && !prefersReducedMotion() ? 'loginChevronPulse 1.4s ease-in-out infinite' : 'none',
      '@keyframes loginChevronPulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.55 },
      },
    }}
  >
    <polygon points="0,0 10,0 6,10 4,10" fill={C.textOnFill} />
  </Box>
);

/** The whole site lives behind this gate: checks for a session, then
 * whether that character's public corp/alliance data clears membership.
 * The unauthenticated and denied states sit inside the big ambient
 * WormholeGate ring — content (the login button / the logout action) lives
 * directly in the gate's void, always visible, never behind a loading wait;
 * the ring's slow spin is pure atmosphere, confined to this pre-auth
 * screen — the dashboard past it stays plain and scannable. Hovering or
 * focusing the login button "locks" the gate: WormholeGate's glyph panels
 * fill in scattered order and the ring pauses mid-spin, then reverses on
 * mouse-leave/blur.
 *
 * NOTE: MUI sx breakpoint-object font sizes (`fontSize: { xs: ..., sm: ... }`)
 * did not resolve correctly on this page during testing — fell back to the
 * variant's huge default instead of the override, causing severe overflow.
 * Root cause not fully isolated; `clamp()` sidesteps it reliably and reads
 * better anyway (smooth scaling, no hard breakpoint jump). Prefer clamp()
 * over breakpoint objects for font-size on this page until that's understood. */
const Start: React.FC = () => {
  const { me, login, logout, loading } = useAuth();
  const [locking, setLocking] = useState(false);

  if (loading) {
    return (
      <>
        <WormholeVoid />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Typography sx={{ fontFamily: FONT.mono, color: C.wormhole, letterSpacing: 2 }}>
            SCANNING REGISTRY&hellip;
          </Typography>
        </Box>
      </>
    );
  }

  if (!me.authenticated) {
    return (
      <>
      <WormholeVoid />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, py: { xs: 3, sm: 6 }, gap: { xs: 2, sm: 3 } }}>
        {/* Hero banner: a one-time "link acquired" status line boots in
            first, then the title follows — a two-beat sequence instead of
            everything fading in at once, the cheapest way to make an
            entrance read as a system coming online rather than a page just
            loading. Both are one-shot reveals, never looping — this page's
            standing motion budget is the ring's spin/parallax and the
            backdrop's twinkle/parallax; the text itself only ever animates
            once, on arrival. */}
        <Typography
          sx={{
            fontFamily: FONT.mono,
            fontSize: 'clamp(0.68rem, 2.2vw, 0.82rem)',
            letterSpacing: '0.25em',
            color: hexToRgba(C.cyan, 0.85),
            textTransform: 'uppercase',
            animation: prefersReducedMotion() ? 'none' : 'heroStatusReveal 0.5s ease-out both',
            '@keyframes heroStatusReveal': {
              from: { opacity: 0, transform: 'translateY(6px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          &gt; wormhole network link — signal acquired
        </Typography>

        {/* v10: the ring itself is untouched by direct instruction — the
            page around it moves to the LCARS lettering voice, so the first
            screen and the console behind it read as one system. */}
        <Typography
          component="h1"
          sx={{
            fontFamily: FONT.lcars,
            fontWeight: 700,
            fontSize: 'clamp(2.2rem, 10vw, 5.2rem)',
            color: C.wormhole,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            lineHeight: 0.95,
            textAlign: 'center',
            maxWidth: '92vw',
            textShadow: glow(C.wormhole, 20, 0.4),
            animation: prefersReducedMotion()
              ? 'none'
              : 'heroTitleReveal 0.7s cubic-bezier(0.16,1,0.3,1) 150ms both, heroTitleGlow 4s ease-in-out 0.85s infinite',
            '@keyframes heroTitleReveal': {
              from: { opacity: 0, transform: 'translateY(14px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
            '@keyframes heroTitleGlow': {
              '0%, 100%': { textShadow: glow(C.wormhole, 20, 0.4) },
              '50%': { textShadow: glow(C.wormhole, 34, 0.65) },
            },
          }}
        >
          Wormhole Gobbo Buddy
        </Typography>

        <WormholeGate locking={locking}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => login(currentPath())}
            onMouseEnter={() => setLocking(true)}
            onMouseLeave={() => setLocking(false)}
            onFocus={() => setLocking(true)}
            onBlur={() => setLocking(false)}
            startIcon={<LoginChevron pulse={locking} />}
            sx={{
              // Fixed footprint: no property on this button may change on
              // hover/focus that affects layout size. The prior version
              // animated `letterSpacing` (0.1em -> 0.16em) on hover, which
              // changes the rendered text's width — since the button has no
              // fixed width, that widened the whole button and visibly
              // resized/jumped it every time a pointer entered it, centered
              // inside the ring via flex, so the jump was doubly obvious.
              // `transform: scale(...)` has the same problem (it visually
              // enlarges the box even though it doesn't reflow), so the fix
              // drops scale too — the only hover feedback left is a pure
              // position shift (`translateY`, which moves but never resizes)
              // plus a brighter glow, exactly the two properties that can
              // change here without the button's box ever changing size.
              px: 4,
              py: 1.4,
              fontSize: '0.95rem',
              letterSpacing: '0.1em',
              boxShadow: glow(C.gold, 18, 0.42),
              animation: prefersReducedMotion() ? 'none' : 'ctaIdlePulse 2.8s ease-in-out infinite',
              transition: 'box-shadow 0.3s ease, transform 0.3s ease',
              '&:hover, &:focus-visible': {
                animationPlayState: 'paused',
                boxShadow: glow(C.gold, 32, 0.75),
                transform: 'translateY(-2px)',
              },
              '@keyframes ctaIdlePulse': {
                '0%, 100%': { boxShadow: glow(C.gold, 18, 0.42) },
                '50%': { boxShadow: glow(C.gold, 30, 0.6) },
              },
            }}
          >
            Authenticate via EVE SSO
          </Button>
        </WormholeGate>

        {/* Tagline as a bracketed readout rather than a bare line of text —
            two short hairline ticks flanking it, echoing the ring's own
            bracket/edge language instead of introducing a new shape. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            maxWidth: 'min(460px, 92vw)',
            animation: prefersReducedMotion() ? 'none' : 'heroTaglineReveal 0.6s ease-out 0.4s both',
            '@keyframes heroTaglineReveal': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Box sx={{ width: 18, height: '1px', backgroundColor: hexToRgba(C.wormhole, 0.4), flexShrink: 0 }} />
          <Typography sx={{ color: C.textDim, textAlign: 'center', fontSize: 'clamp(0.8rem, 3vw, 1rem)' }}>
            Nobody remembers a static off the top of their head. Log in with EVE and let the goblin do it.
          </Typography>
          <Box sx={{ width: 18, height: '1px', backgroundColor: hexToRgba(C.wormhole, 0.4), flexShrink: 0 }} />
        </Box>
      </Box>
      </>
    );
  }

  if (!me.isMember) {
    return (
      <>
      <WormholeVoid />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, py: { xs: 3, sm: 6 }, gap: { xs: 2, sm: 3 } }}>
        <Avatar
          src={me.character ? `https://images.evetech.net/characters/${me.character.id}/portrait?size=128` : undefined}
          sx={{ width: 88, height: 88, filter: 'grayscale(1)', border: `1px solid ${C.red}` }}
        />

        <WormholeGate accent={C.red}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                bgcolor: hexToRgba(C.red, 0.16),
                border: `1px solid ${hexToRgba(C.red, 0.6)}`,
                color: C.red,
                fontFamily: FONT.lcars,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                fontSize: '0.8rem',
                py: 1,
                px: 2,
                borderRadius: 999,
                boxShadow: glow(C.red, 16, 0.25),
                whiteSpace: 'nowrap',
              }}
            >
              No Registry Match
            </Box>
            <Button variant="outlined" onClick={logout} size="small" sx={{ color: C.textDim, borderColor: C.borderSub }}>
              Log out
            </Button>
          </Box>
        </WormholeGate>

        <Typography sx={{ color: C.textDim, textAlign: 'center', maxWidth: 'min(420px, 92vw)', fontSize: 'clamp(0.8rem, 3vw, 1rem)' }}>
          {me.character?.name}&rsquo;s signature doesn&rsquo;t clear the corp or alliance registry. This channel stays
          sealed.
        </Typography>
      </Box>
      </>
    );
  }

  return <Gate />;
};

export default Start;
